import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/format";
import {
  LayoutDashboard, BookOpen, ScrollText, HandCoins, ReceiptText, PiggyBank,
  Award, Users, Building2, HeartHandshake, Target, FileBarChart, Sparkles, LogOut, Landmark, CalendarClock, FolderOpen, ShieldCheck, UserCog, Briefcase, ShieldAlert, Gavel, TrendingUp, Package, LineChart, Coins, FileClock, Heart,
} from "lucide-react";

const NAV = [
  { section: "Overview", items: [
    { to: "/", label: "Control Center", icon: LayoutDashboard, end: true },
    { to: "/reports", label: "Reports & Analytics", icon: FileBarChart },
    { to: "/board-meetings", label: "Board Meetings", icon: CalendarClock },
    { to: "/insights", label: "AI Insights", icon: Sparkles },
  ]},
  { section: "Accounting", items: [
    { to: "/accounts", label: "Chart of Accounts", icon: BookOpen },
    { to: "/ledger", label: "General Ledger", icon: ScrollText },
    { to: "/budget", label: "Budget", icon: Target },
    { to: "/funds", label: "Fund Accounting", icon: PiggyBank },
  ]},
  { section: "Transactions", items: [
    { to: "/revenue", label: "Revenue & Donations", icon: HandCoins },
    { to: "/expenses", label: "Expenses", icon: ReceiptText },
    { to: "/grants", label: "Grants", icon: Award },
    { to: "/bank-reconciliation", label: "Bank Reconciliation", icon: Landmark },
  ]},
  { section: "Records", items: [
    { to: "/donors", label: "Donor Master", icon: HeartHandshake },
    { to: "/vendors", label: "Vendor Master", icon: Building2 },
    { to: "/programs", label: "Programs", icon: Users },
    { to: "/documents", label: "Documents", icon: FolderOpen },
  ]},
  { section: "Assets & Treasury", items: [
    { to: "/assets", label: "Fixed Assets", icon: Package },
    { to: "/investments", label: "Investments", icon: LineChart },
    { to: "/receivables", label: "Pledges & Receivables", icon: Coins },
    { to: "/payables", label: "Payables Aging", icon: FileClock },
  ]},
  { section: "Operations & Governance", items: [
    { to: "/payroll", label: "Payroll & Personnel", icon: Briefcase },
    { to: "/compliance", label: "Compliance & Risk", icon: ShieldAlert },
    { to: "/governance", label: "Board Governance", icon: Gavel },
    { to: "/strategy", label: "Strategic Planning", icon: TrendingUp },
  ]},
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const nav = [...NAV];
  const adminItems = [];
  if (user?.role === "admin") adminItems.push({ to: "/admin/users", label: "User Management", icon: UserCog });
  if (user?.role === "admin" || user?.role === "auditor") adminItems.push({ to: "/admin/audit", label: "Audit Trail", icon: ShieldCheck });
  if (adminItems.length) nav.push({ section: "Administration", items: adminItems });

  return (
    <div className="min-h-screen flex bg-bone">
      {/* Sidebar */}
      <aside className="w-64 bg-forest text-white flex flex-col fixed h-screen z-20" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
              <Heart className="h-5 w-5 text-white" fill="currentColor" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-base font-semibold">Harmony House</div>
              <div className="text-[10px] tracking-widest text-forest-muted uppercase">IFMS v1.0</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
          {nav.map((grp) => (
            <div key={grp.section}>
              <div className="px-3 mb-1.5 text-[10px] uppercase tracking-widest text-forest-muted font-medium">
                {grp.section}
              </div>
              <div className="space-y-0.5">
                {grp.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                        isActive ? "bg-white/12 text-white font-medium" : "text-forest-muted hover:text-white hover:bg-white/6"
                      }`
                    }
                  >
                    <it.icon className="h-4 w-4 shrink-0" />
                    {it.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center text-xs font-semibold">
              {(user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[10px] text-forest-muted">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
            <button onClick={logout} data-testid="logout-btn" title="Sign out"
              className="text-forest-muted hover:text-white transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 min-h-screen">
        <div className="max-w-7xl mx-auto px-8 py-8">{children}</div>
      </main>
    </div>
  );
}
